import { type RefObject, useEffect } from 'react'

// これより短いスワイプはタップや誤操作として無視する (px)
const SWIPE_THRESHOLD_PX = 40

type Params = {
  targetRef: RefObject<HTMLElement | null>
  enabled: boolean
  axis: 'x' | 'y'
  /** 右→左で読むとき。指を右へ動かすと次のページへ進む */
  reversed: boolean
  onNext: () => void
  onPrev: () => void
}

/** 1 本指のスワイプを、指を離した時点で次/前のページへの移動に変える（アニメーションなしのページめくり用） */
export const usePageSwipe = ({ targetRef, enabled, axis, reversed, onNext, onPrev }: Params) => {
  useEffect(() => {
    const el = targetRef.current
    if (!enabled || !el) return

    let start: { x: number; y: number } | null = null

    const handleStart = (e: TouchEvent) => {
      const touch = e.touches.length === 1 ? e.touches.item(0) : null
      start = touch ? { x: touch.clientX, y: touch.clientY } : null
    }
    const handleMove = (e: TouchEvent) => {
      // ピンチなど 2 本指になったらスワイプとして扱わない
      if (e.touches.length > 1) start = null
    }
    const handleEnd = (e: TouchEvent) => {
      const touch = e.changedTouches.item(0)
      if (!start || !touch || e.touches.length > 0) return
      const dx = touch.clientX - start.x
      const dy = touch.clientY - start.y
      start = null
      const main = axis === 'x' ? dx : dy
      const cross = axis === 'x' ? dy : dx
      if (Math.abs(main) < SWIPE_THRESHOLD_PX || Math.abs(main) < Math.abs(cross)) return
      const isForward = reversed ? main > 0 : main < 0
      if (isForward) onNext()
      else onPrev()
    }

    el.addEventListener('touchstart', handleStart, { passive: true })
    el.addEventListener('touchmove', handleMove, { passive: true })
    el.addEventListener('touchend', handleEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', handleStart)
      el.removeEventListener('touchmove', handleMove)
      el.removeEventListener('touchend', handleEnd)
    }
  }, [targetRef, enabled, axis, reversed, onNext, onPrev])
}
